import { Check } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface StepperStep {
  id: string;
  label: string;
  description?: string;
}

interface StepperProps {
  steps: StepperStep[];
  currentStep: number;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  /** Variante compacte pour les en-têtes de funnels (petits indicateurs) */
  variant?: 'default' | 'compact';
}

export function Stepper({
  steps,
  currentStep,
  className,
  orientation = 'horizontal',
  variant = 'default',
}: StepperProps) {
  /* ── Compact mode for funnel headers ─────────────────────── */
  if (variant === 'compact') {
    return (
      <div
        role="progressbar"
        // 1-based, aligné sur FunnelStepper : « Étape X sur Y » lisible par les AT.
        aria-valuenow={currentStep + 1}
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-label={`Étape ${currentStep + 1} sur ${steps.length}${steps[currentStep] ? ` — ${steps[currentStep].label}` : ''}`}
        className={cn('flex flex-col gap-2', className)}
      >
        <div className="flex items-center">
          {steps.map((step, i) => {
            const active = i === currentStep;
            const done = i < currentStep;
            return (
              <div key={step.id} className={cn('flex items-center', i > 0 && 'flex-1')}>
                {i > 0 && (
                  <div
                    className={cn(
                      'h-px flex-1 transition-colors duration-500',
                      done ? 'bg-brand-400' : 'bg-stone-200',
                    )}
                  />
                )}
                <div className="flex shrink-0 items-center gap-1.5">
                  <div
                    className={cn(
                      'flex h-[22px] w-[22px] items-center justify-center rounded-full text-[9px] font-bold ring-2 transition-all duration-300',
                      active
                        ? 'scale-110 bg-brand-600 text-white ring-brand-200'
                        : done
                          ? 'bg-brand-500 text-white ring-transparent'
                          : 'bg-stone-100 text-stone-400 ring-transparent',
                    )}
                  >
                    {done ? <Check className="h-3 w-3" strokeWidth={2.5} /> : i + 1}
                  </div>
                  <span
                    className={cn(
                      'hidden text-[11px] font-medium leading-none transition-colors duration-300 sm:block',
                      active ? 'text-brand-700' : done ? 'text-stone-500' : 'text-stone-400',
                    )}
                  >
                    {step.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {/* Contexte mobile : labels individuels cachés sous sm → on affiche l'étape courante */}
        {steps[currentStep] && (
          <p className="text-[11px] font-medium text-brand-700 sm:hidden">
            Étape {currentStep + 1}/{steps.length} · {steps[currentStep].label}
          </p>
        )}
      </div>
    );
  }
  if (orientation === 'vertical') {
    return (
      <ol
        aria-label="Étapes"
        className={cn('flex flex-col gap-0', className)}
      >
        {steps.map((step, idx) => {
          const state =
            idx < currentStep ? 'completed' : idx === currentStep ? 'active' : 'pending';
          const isLast = idx === steps.length - 1;
          return (
            <li key={step.id} className="relative flex gap-4">
              {/* Connector */}
              {!isLast && (
                <span
                  aria-hidden
                  className={cn(
                    'absolute left-[15px] top-[30px] w-px',
                    state === 'completed' ? 'bg-brand-500' : 'bg-stone-200',
                    'bottom-0',
                  )}
                />
              )}
              {/* Indicator */}
              <div className="flex shrink-0 flex-col items-center">
                <span
                  aria-hidden
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ring-2 ring-offset-2 ring-offset-paper transition-all duration-150',
                    state === 'completed' && 'bg-brand-600 text-white ring-brand-600',
                    state === 'active' && 'bg-paper text-brand-700 ring-brand-500',
                    state === 'pending' && 'bg-stone-100 text-stone-400 ring-stone-200',
                  )}
                >
                  {state === 'completed' ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </span>
              </div>
              {/* Label */}
              <div className={cn('pb-7', isLast && 'pb-0')}>
                <p
                  className={cn(
                    'text-sm font-medium leading-8',
                    state === 'active' ? 'text-ink' : state === 'completed' ? 'text-stone-700' : 'text-stone-400',
                  )}
                >
                  {step.label}
                </p>
                {step.description && (
                  <p className="text-xs text-stone-500 leading-relaxed">{step.description}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    );
  }

  /* Horizontal (desktop default) */
  return (
    <ol
      aria-label="Étapes"
      className={cn('flex items-center justify-between gap-0', className)}
    >
      {steps.map((step, idx) => {
        const state =
          idx < currentStep ? 'completed' : idx === currentStep ? 'active' : 'pending';
        const isLast = idx === steps.length - 1;
        return (
          <li key={step.id} className={cn('relative flex flex-1 items-center', isLast && 'flex-none')}>
            {/* Connector */}
            {!isLast && (
              <span
                aria-hidden
                className={cn(
                  'absolute left-8 right-0 top-4 h-px',
                  state === 'completed' ? 'bg-brand-500' : 'bg-stone-200',
                )}
              />
            )}
            <div className="flex flex-col items-center gap-1.5">
              {/* Indicator */}
              <span
                aria-hidden
                className={cn(
                  'relative flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ring-2 ring-offset-2 ring-offset-paper transition-all duration-150',
                  state === 'completed' && 'bg-brand-600 text-white ring-brand-600',
                  state === 'active' && 'bg-paper text-brand-700 ring-brand-500',
                  state === 'pending' && 'bg-stone-100 text-stone-400 ring-stone-200',
                )}
              >
                {state === 'completed' ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </span>
              {/* Label */}
              <span
                className={cn(
                  'text-center text-[11px] font-medium tracking-wide whitespace-nowrap',
                  state === 'active' ? 'text-brand-700' : state === 'completed' ? 'text-stone-600' : 'text-stone-400',
                )}
              >
                {step.label}
              </span>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
